import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { MsalGuard, MsalService } from '@azure/msal-angular';
import { SilentRequest, AuthenticationResult } from '@azure/msal-browser';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { CommonService } from 'src/app/services/common/common.service';
import { CoreService } from 'src/app/services/core/core.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class HomeGuard {
  /**
   * constructor
   * @param router
   */
  constructor(
    private commonService: CommonService,
    private msalGuard: MsalGuard,
    private router: Router,
    private coreService: CoreService,
    private msalService: MsalService
  ) {}

  /**
   * canActivate
   * @returns
   */
  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    if (this.msalGuard.canActivate(next, state)) {
      return this.checkUserPermission();
    } else {
      return false;
    }
  }

  /**
   * checkUserPermission
   * @returns
   */
  checkUserPermission(): Observable<boolean> {
    return this.commonService.userProfileData.pipe(
      switchMap((res: any) => {
        if (res) {
          return of(this.checkUserData(res));
        } else {
          this.commonService.showLoading();
          return this.coreService.getUserProfile().pipe(
            switchMap((res) => {
              if (res) {
                this.commonService.hideLoading();
                this.commonService.userProfileData.next(res);
                if (res?.data.userProfileThumbnailImageBlobUrl || res?.data.userProfileImageBlobUrl) {
                  this.commonService.avatar.next({
                    image: res?.data.userProfileThumbnailImageBlobUrl ?? res?.data.userProfileImageBlobUrl
                  });
                }
                return of(this.checkUserData(res));
              } else {
                this.commonService.hideLoading();
                return of(true);
              }
            })
          );
        }
      })
    );
  }

  /**
   *
   * @param res
   * @returns
   */
  checkUserData(res: any): boolean {
    const automotiveServices = res?.data?.userPermission?.automotiveServices;
    const orgPackages = res?.data?.userPermission?.orgPackages;
    const userDetail = res?.data?.roleDetail;
    const isRepairLite = userDetail?.some(r => r.automotiveServiceName === 'Addenda Repair' && r.labelName === 'Addenda Repair Lite');
    this.commonService.setPermission(automotiveServices);

    const repair = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'addenda repair' && x.labelName != 'Addenda Repair Lite');
    const quote = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'carheal quote');
    const claim = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'addenda claims');
    const claimSurveyor = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'claim surveyor');
    const fnol = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'fnol');
    const quoteAssessment = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'quote assessment');
    const inventory = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'addenda inventory');
    const auto = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'addenda auto');
    const accounting = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'accounting');
    const hrm = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'hrms');
    const hasDashboardFeaturePackage = orgPackages
      ?.find(x => x.automotiveServiceName.toLowerCase() === 'analytics')
      ?.permissions?.some(x => x.permissionName?.toLowerCase() === 'dashboard');
    const addendaHealth = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'addenda health');
    const addendaFacility = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'addenda servescape');
    const addendaProperty = automotiveServices?.find(x => x.automotiveServiceName.toLowerCase() === 'addenda property');
    if (repair) {
      const repairRole = repair.roleName.toLowerCase();
      const quoteRole = quote?.roleName.toLowerCase();
      if (repairRole === 'bodyshop manager' || quoteRole === 'bodyshop manager') {
        const hasDashboardPermission =
          repair.permissions.some(x => x.permissionName === 'dashboard') || quote?.permissions.some(x => x.permissionName === 'dashboard');
        if (isRepairLite || !hasDashboardPermission) {
          this.router.navigate([ 'repair' ]);
          return false;
        } else if (!hasDashboardFeaturePackage) {
          this.router.navigate([ 'repair/booking-journal' ]);
          return false;
        }
        return true;
      } else if (repair.roleName.toLowerCase() === 'service desk') {
        if (this.commonService.checkPermission('Addenda Repair', 'outbound.lounge')) {
          this.router.navigate([ 'repair/lounge' ]);
        } else {
          this.router.navigate([ 'repair/workflow-lounge' ]); //workflow.lounge
        }
        return false;
      } else if (repair.roleName.toLowerCase() === 'driver') {
        this.router.navigate([ 'repair/outbound' ]);
        return false;
      } else if (repair.roleName.toLowerCase() === 'technician') {
        this.router.navigate([ 'repair/technician-calendar' ]);
        return false;
      } else {
        this.router.navigate([ 'repair' ]);
        return false;
      }
    } else if (quote) {
      const quoteRole = quote?.roleName.toLowerCase();
      const hasDashboardPermission = quote.permissions.some(x => x.permissionName === 'dashboard');
      if (quoteRole === 'bodyshop manager' && hasDashboardPermission) {
        if (!hasDashboardFeaturePackage) {
          this.router.navigate([ 'quote' ]);
          return false;
        }
        return true;
      } else {
        this.router.navigate([ 'quote' ]);
      }
      return false;
    } else if (claim || claimSurveyor) {
      this.router.navigate([ 'claim' ]);
      return false;
    } else if (fnol) {
      this.router.navigate([ 'fnol' ]);
      return false;
    } else if (quoteAssessment) {
      this.router.navigate([ 'case' ]);
      return false;
    } else if (auto) {
      if (auto.roleName.toLowerCase() === 'agent admin') {
        this.router.navigate([ 'ai-studio' ]);
      } else {
        this.router.navigate([ 'auto' ]);
      }
      return false;
    } else if (inventory) {
      this.router.navigate([ 'inventory' ]);
      return false;
    } else if (addendaHealth || addendaFacility || addendaProperty) {
      this.router.navigate([ 'employee-management' ]);
      return false;
    } else if (accounting) {
      this.router.navigate([ 'accounting' ]);
      return false;
    } else if (hrm) {
      // HRM-only users should land on HRM area instead of being forced to logout.
      this.router.navigate([ 'hrm/attendance' ]);
      return false;
      '';
    } else {
      const request = {
        redirectStartPage: '/',
        scopes: [ 'openid', 'profile', `${environment.b2cSettings.caseManagementAppClientId}` ]
      };
      const activeAccount = this.msalService.instance.getActiveAccount();
      this.msalService.acquireTokenSilent(request as SilentRequest).subscribe({
        next: (result: AuthenticationResult) => {
          this.commonService.clearData();
          this.msalService.logoutRedirect({
            account: activeAccount,
            idTokenHint: result.idToken
          });
        }
      });
      return false;
    }
  }
}
